import { isLlmRouteExecutableProvider } from './executor';
import type { LlmProviderId } from './preferences-v2';
import type { ModelRouteDefinition } from './routes';

export type RouteFitStateKind = 'default' | 'recommended' | 'executable' | 'warning' | 'blocked' | 'stale';

export interface RouteFitState {
  state: RouteFitStateKind;
  label: string;
  className: string;
  detail?: string;
}

export function getRouteFitState(
  route: ModelRouteDefinition,
  selectedProvider: LlmProviderId | '',
  selectedModel: { pricing?: { inputPer1M: number; outputPer1M: number } | null } | null,
  selectedModelIsUnavailable: boolean,
): RouteFitState {
  if (selectedModelIsUnavailable) {
    return {
      state: 'stale',
      label: 'Stale',
      className: 'bg-amber-50 text-amber-700',
      detail: 'Saved model is not in the current catalog.',
    };
  }

  if (selectedProvider && !isLlmRouteExecutableProvider(selectedProvider)) {
    return {
      state: 'blocked',
      label: 'Blocked',
      className: 'bg-red-50 text-red-700',
      detail: 'Provider is not executor-backed yet.',
    };
  }

  if (!selectedProvider || !selectedModel) {
    return {
      state: 'default',
      label: 'Default',
      className: 'bg-neutral-200 text-neutral-700',
    };
  }

  if (!selectedModel.pricing) {
    return {
      state: 'warning',
      label: 'Budget reserve',
      className: 'bg-amber-50 text-amber-700',
      detail: 'Pricing is unknown; budget checks use the unknown-request reserve.',
    };
  }

  const localPreferred = route.modelFitPolicy?.privacyPosture === 'localPreferredCloudAllowed';
  const selectedLocal = selectedProvider === 'ollama' || selectedProvider === 'webllm';
  if (localPreferred && selectedLocal) {
    return {
      state: 'recommended',
      label: 'Recommended',
      className: 'bg-emerald-50 text-emerald-700',
      detail: 'Local runtime matches this route privacy posture.',
    };
  }

  return {
    state: 'executable',
    label: 'Executable',
    className: 'bg-emerald-50 text-emerald-700',
  };
}
